import { inject, Injectable } from '@angular/core';

import {
  CanvasElementDefinitions,
  CanvasElementFrom,
  LayoutData,
} from '@blaze-runtime/engine-domain';
import { sentryLog } from '@blaze-shared/logger-util';
import { deepClone } from '@blaze-shared/shared-util';
import { StyleSheet } from '@blaze-shared/theming-domain';

import { CanvasElementDefinition } from '../../../canvas-elements/element-definition';
import { ElementDefinitionsService } from '../../element-definitions.service';
import { StyleHelperService } from '../../styles';

import { layoutMigrationLog } from './layout-migration-logger';
import { ElementMigration, LayoutMigrationContext } from './layout-migration.models';

@Injectable({
  providedIn: 'root',
})
export class LayoutMigrationRunnerService {
  private readonly elementDefinitionService = inject(ElementDefinitionsService);
  private readonly styleHelperService = inject(StyleHelperService);

  migrateLayoutMutably(layout: LayoutData): void {
    const elements = layout?.uiLayer?.content ?? [];
    if (elements.length === 0) {
      return;
    }
    const migrationContext: LayoutMigrationContext = {
      layoutSnapshot: deepClone(layout),
    };

    for (const element of elements) {
      if (!element.type) {
        continue;
      }

      const definition = this.elementDefinitionService.getDefinition(element.type);
      if (!definition) {
        sentryLog(
          `[Layout migrations] Missing element definition for type '${element.type}', skipping migrations`,
          'warning',
          { jsonPayload: { elementId: element.id, element: element } },
        );
        continue;
      }
      const migrations = this.getSortedMigrations(definition);
      if (migrations?.length) {
        this.applyLayoutMigrations(element, migrations, migrationContext);
      }
    }
  }

  migrateStyleSheets(styleSheets: StyleSheet[]): StyleSheet[] {
    return styleSheets.map((styleSheet) => {
      const elementType = styleSheet.elementType as keyof CanvasElementDefinitions | undefined;
      if (!elementType) {
        return styleSheet;
      }

      const definition = this.elementDefinitionService.getDefinition(elementType);
      if (!definition) {
        sentryLog(
          `[Style migrations] Missing element definition for type '${elementType}', skipping migrations`,
          'warning',
          { jsonPayload: { styleSheet } },
        );
        return styleSheet;
      }

      const migrations = this.getSortedMigrations(definition);
      if (!migrations?.length) {
        return styleSheet;
      }

      const affectedStyleSheet = deepClone(styleSheet);
      this.applyStyleSheetsMigrations(affectedStyleSheet, migrations, definition);
      return affectedStyleSheet;
    });
  }

  private applyLayoutMigrations(
    element: CanvasElementFrom<any>,
    migrations: Array<{ version: number; migration: ElementMigration }>,
    context: LayoutMigrationContext,
  ): void {
    const currentVersion = element.version ?? 0;

    const migrationsToApply = migrations.filter((m) => m.version > currentVersion);
    if (migrationsToApply.length === 0) {
      return;
    }

    for (const { migration, version } of migrationsToApply) {
      try {
        layoutMigrationLog(
          'info',
          'LayoutMigrationRunnerService',
          'applyLayoutMigrations',
          (tag) => {
            console.log(
              `${tag} ${element.type} migration: v${version} ${migration.description} ${element.id}`,
            );
          },
        );

        if (migration.mode === 'props') {
          migration.run(element.props);
        } else {
          migration.run(element, context);
        }
        element.version = version;
      } catch (error) {
        layoutMigrationLog(
          'error',
          'LayoutMigrationRunnerService',
          'applyLayoutMigrations',
          (tag) => {
            console.error(`${tag} Migration ${version} failed on element ${element.id}:`, error);
          },
        );
        throw error;
      }
    }
  }

  private applyStyleSheetsMigrations(
    styleSheet: StyleSheet,
    migrations: Array<{ version: number; migration: ElementMigration }>,
    definition: CanvasElementDefinition,
  ): void {
    const currentVersion = styleSheet.version ?? 0;

    const migrationsToApply = migrations.filter((m) => m.version > currentVersion);
    if (migrationsToApply.length === 0) {
      return;
    }

    const styleName = styleSheet?.themeId
      ? `${styleSheet.elementType}::${styleSheet.name}`
      : styleSheet.id;
    for (const { version, migration } of migrationsToApply) {
      try {
        layoutMigrationLog(
          'info',
          'LayoutMigrationRunnerService',
          'applyStyleSheetsMigrations',
          (tag) => {
            console.log(`${tag} ${styleName} migration: v${version} ${migration.description}`);
          },
        );

        styleSheet.data = styleSheet.data.map((data) => {
          if (migration.mode !== 'props') {
            return data;
          }

          migration.run(data);
          return this.styleHelperService.extractStyleProps(data, definition.type, false);
        });
        styleSheet.version = version;
      } catch (error) {
        layoutMigrationLog(
          'error',
          'LayoutMigrationRunnerService',
          'applyStyleSheetsMigrations',
          (tag) => {
            console.error(`${tag} Migration ${version} failed on style ${styleName}:`, error);
          },
        );
      }
    }
  }

  private getSortedMigrations(
    definition: CanvasElementDefinition,
  ): Array<{ version: number; migration: ElementMigration }> {
    const migrations = definition.migrations ?? {};
    return Object.entries(migrations)
      .map(([k, migration]) => ({ version: Number(k), migration }))
      .filter(({ version }) => {
        if (Number.isFinite(version)) {
          return true;
        }
        layoutMigrationLog(
          'error',
          'LayoutMigrationRunnerService',
          'getSortedMigrations',
          (tag) => {
            console.error(`${tag} Invalid migration key for ${definition.type}, skipping`);
          },
        );
        return false;
      })
      .sort((a, b) => a.version - b.version);
  }
}
